import * as React from 'react';

import {type Line} from '../../util/plotHelpers/types';
import PlotWarning from '../PlotWarning';
import {MULTI_METRIC_MAX_RUNS} from './defaults';
import {usePanelConfigContext} from './PanelConfigContext';
import {usePanelGroupingSettings} from './RunsLinePlotContext/usePanelGroupingSettings';

/**
 * Warning priority — first match returns:
 *
 *   1. multi-metric throttled (metrics × maxRuns > MULTI_METRIC_MAX_RUNS)
 *      Per-metric line count is reduced to fit the overall budget.
 *
 *   2. grouped + hasSubsampledGroups
 *      Backend reports at least one group had runs excluded from aggregation.
 *      Authoritative signal from the grouped query pipeline.
 *
 *   3. multi-runset (runsetCount > 1) + any runset hit its cap
 *      Reports can have multiple runsets; the backend query applies the cap
 *      **per runset**. When maxFetchedRunCount (max count across runsets)
 *      reaches the cap the single-runset wording "Showing up to N runs"
 *      is misleading — the user may be seeing N × runsetCount lines. The
 *      "per run set" variant clarifies which knob moves the cap. Applies to
 *      both grouped and ungrouped modes.
 *
 *   4. grouped + runCount >= maxGroupRuns
 *      In client-side grouping the frontend fetches a pool of up to
 *      maxGroupRuns raw runs and forms groups from them; a filled pool means
 *      the groups were built from a sample. `>=` tolerates any overshoot
 *      (e.g. baseline/pinned runs injected above the cap or cross-runset
 *      summation) so the warning isn't silently dropped.
 *
 *   5. grouped + distinct group count >= maxRuns in full fidelity,
 *      or lines.length > maxRuns in sampled history.
 *
 *   6. ungrouped + runCount >= maxRuns
 *      Query hit its row limit. Without a backend project total we can't
 *      distinguish this from "exactly maxRuns matching runs", so the warning
 *      fires in both cases — helpText "raise the max" works either way.
 */
export const LinePlotLegendWarning = React.memo(
  ({
    disallowSelect,
    hasSubsampledGroups,
    isMultiMetric,
    isReadOnly,
    lines,
    loading,
    maxFetchedRunCount,
    maxGroupRuns,
    metrics,
    runCount,
    runsetCount,
    totalGroupedIncludedRuns,
  }: {
    disallowSelect: boolean;
    hasSubsampledGroups: boolean;
    isMultiMetric: boolean;
    isReadOnly: boolean;
    lines: Line[];
    loading: boolean;
    maxFetchedRunCount: number;
    maxGroupRuns: number;
    metrics: number;
    runCount: number;
    runsetCount: number;
    totalGroupedIncludedRuns: number;
  }) => {
    const {isAnyRunsetGrouped: isGrouped} = usePanelGroupingSettings();
    const {
      groupingDepth,
      isFullFidelity,
      limit: maxRuns,
    } = usePanelConfigContext();
    const isGroupLimitReached = isFullFidelity
      ? new Set(lines.map(line => line.uniqueId)).size >= maxRuns
      : lines.length > maxRuns;
    const isGroupingDepthEnabled =
      isFullFidelity && Number.isFinite(groupingDepth);
    const isThrottledMultiMetrics =
      isMultiMetric && metrics * maxRuns > MULTI_METRIC_MAX_RUNS;

    if (isThrottledMultiMetrics && (!loading || lines.length > 0)) {
      const visualizedRuns = Math.floor(MULTI_METRIC_MAX_RUNS / metrics);
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to adjust the number of visualized metrics`
              : undefined
          }
          message={`Limited to ${visualizedRuns} runs for each visualized metric`}
          disallowSelect={disallowSelect}
          type="multi-metric"
        />
      );
    }

    // per-group subsampling detected from backend totalRuns/includedRuns fields
    if (isGrouped && hasSubsampledGroups && !isGroupingDepthEnabled) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Not all runs in every group were included in the aggregation.`
              : undefined
          }
          message={`Only ${totalGroupedIncludedRuns} runs are grouped`}
          disallowSelect={disallowSelect}
          type="group-subsampling"
        />
      );
    }

    // Multi-runset per-runset cap: reports can stack multiple runsets into one
    // panel, and the backend query applies maxRuns/maxGroupRuns **per runset**.
    // The rendered panel is the union, so `runCount` (cross-runset sum) can
    // hit the cap when no individual runset has. Keying off maxFetchedRunCount
    // (max over per-runset counts) fires only when at least one runset is
    // actually capped, and the "per run set" wording clarifies which knob
    // moves the limit. Ordered before the single-runset branches so the
    // multi-runset phrasing wins when both would match.
    const isMultiRunset = runsetCount > 1;
    if (isMultiRunset && isGrouped && maxFetchedRunCount >= maxGroupRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the number of runs sampled in group metrics`
              : undefined
          }
          message={`Showing groups from up to ${maxGroupRuns} runs per run set`}
          disallowSelect={disallowSelect}
          type="grouped-runs-exceed-max-runs"
        />
      );
    }

    if (isMultiRunset && !isGrouped && maxFetchedRunCount >= maxRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the max number of runs displayed`
              : undefined
          }
          message={`Showing up to ${maxRuns} runs per run set`}
          disallowSelect={disallowSelect}
          type="number-of-runs-exceed-max-runs"
        />
      );
    }

    // Client-grouping sample-pool cap: in client-side grouping mode the frontend
    // fetches a pool of up to maxGroupRuns raw runs and forms groups from them.
    // If we filled the pool, more matching runs likely exist in the project —
    // the visible groups were assembled from a sample, not the full population.
    // (Backend-grouping subsampling is handled above via hasSubsampledGroups.)
    if (isGrouped && runCount >= maxGroupRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the number of runs sampled in group metrics`
              : undefined
          }
          message={`Showing groups from up to ${maxGroupRuns} runs`}
          disallowSelect={disallowSelect}
          type="grouped-runs-exceed-max-runs"
        />
      );
    }

    if (isGrouped && isGroupLimitReached) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the max number of groups displayed`
              : undefined
          }
          message={`Showing first ${maxRuns} groups`}
          disallowSelect={disallowSelect}
          type="grouped-runs-exceed-max-groups"
        />
      );
    }

    /**
     * Ungrouped cap indicator: the backend query limit is maxRuns, so when
     * runCount hits maxRuns the query was capped. Without a backend project-
     * total count, we can't distinguish "capped with more available" from
     * "exactly this many runs match" — this fires in both cases. The helpText
     * ("raise the max") is a valid action either way, keeping false-positive
     * cost low.
     */
    if (!isGrouped && runCount >= maxRuns) {
      return (
        <PlotWarning
          helpText={
            !isReadOnly
              ? `Click on the gear icon (top right corner of this chart) to change the max number of runs displayed`
              : undefined
          }
          message={`Showing up to ${maxRuns} runs`}
          disallowSelect={disallowSelect}
          type="number-of-runs-exceed-max-runs"
        />
      );
    }

    return null;
  }
);

LinePlotLegendWarning.displayName = 'LinePlotLegendWarning';
